import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ArrowRightIcon, EyeOffIcon } from '@/assets/icons';
import { useViewerProfile } from '@/auth/useViewerProfile';
import { Button } from '@/components';
import { Banner } from '@/components/molecules';
import { BANNER_REGISTRY, bannerLanguageFromI18n, pickBannerCopy } from '@/config/banners';
import { RoutesEnum } from '@/enums';
import { useDismissBannerMutation } from '@/store/api/users';

const HIDDEN_PATHS: readonly string[] = [RoutesEnum.LOGIN, RoutesEnum.ONBOARDING];

const isHiddenPath = (pathname: string) =>
  HIDDEN_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));

export const AuthenticatedBanners: FC = () => {
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation('components');
  const { me, dismissedBanners, isProfileLoading, isProfileError } = useViewerProfile();
  const [dismissBanner, { isLoading: isDismissRequestPending }] = useDismissBannerMutation();
  const [dismissErrorForId, setDismissErrorForId] = useState<string | null>(null);
  const [locallyDismissedBannerIds, setLocallyDismissedBannerIds] = useState<string[]>([]);

  const effectiveDismissedBannerIds = useMemo(
    () => Array.from(new Set([...dismissedBanners, ...locallyDismissedBannerIds])),
    [dismissedBanners, locallyDismissedBannerIds]
  );

  const activeBanner = useMemo(() => {
    for (const config of BANNER_REGISTRY) {
      if (!effectiveDismissedBannerIds.includes(config.id)) {
        return config;
      }
    }
    return null;
  }, [effectiveDismissedBannerIds]);

  const locale = bannerLanguageFromI18n(i18n.language);
  const copy = activeBanner ? pickBannerCopy(activeBanner, locale) : null;

  const onDismiss = () => {
    if (!activeBanner) {
      return;
    }

    const bannerId = activeBanner.id;
    setDismissErrorForId(null);
    setLocallyDismissedBannerIds(previous => [...new Set([...previous, bannerId])]);

    void dismissBanner(bannerId)
      .unwrap()
      .catch(() => {
        setLocallyDismissedBannerIds(previous => previous.filter(id => id !== bannerId));
        setDismissErrorForId(bannerId);
      });
  };

  const onCtaClick = () => {
    window.open(copy?.ctaLink, '_blank', 'noopener,noreferrer');
  };

  if (
    isHiddenPath(pathname) ||
    isProfileLoading ||
    isProfileError ||
    !me ||
    !activeBanner ||
    !copy
  ) {
    return null;
  }

  return (
    <div className="w-full">
      <Banner
        title={copy.title}
        description={copy.body}
        rightButton={
          <Button
            onClick={onCtaClick}
            variant="secondary"
            size="sm"
            rightIcon={<ArrowRightIcon width={20} height={20} />}
          >
            {copy.ctaLabel}
          </Button>
        }
        bottomButton={
          <Button
            onClick={onDismiss}
            variant="teritary"
            size="sm"
            leftIcon={<EyeOffIcon width={20} height={20} />}
            disabled={isDismissRequestPending}
          >
            {copy.dismissLabel}
          </Button>
        }
        variant="neutral"
        className="border-0 border-b border-b-custom-warm-grey-200"
      />
      {dismissErrorForId === activeBanner.id ? (
        <p
          className="bg-custom-warm-grey-100 px-4 py-2 text-body-14-regular text-red-600"
          role="alert"
        >
          {t('globalBanner.dismissFailed')}
        </p>
      ) : null}
    </div>
  );
};
